import { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { AlertTriangle, ChevronRight, Loader2, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, SectionHeading } from "@/components/intents/guidance-primitives";
import { cn } from "@/lib/utils";
import {
  useTool,
  useTools,
  type IntentReference,
  type Tool,
  type ToolUnavailable,
} from "@/hooks/use-intent-hub";

function Fields({ label, items }: { label: string; items: Tool["inputs"] }) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
        {label}
      </p>
      <ul className="space-y-0.5 text-xs">
        {items.map((f) => (
          <li key={f.name} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono">
              {f.name}
              {f.required && <span className="text-destructive">*</span>}
            </span>
            <span className="text-muted-foreground">{f.type}</span>
            {f.options && (
              <span className="text-muted-foreground font-mono">
                [{f.options.join(" | ")}]
              </span>
            )}
            {f.description && (
              <span className="text-foreground/70 basis-full">
                {f.description}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One tool, collapsed to a line; expands to its description and fields. */
export function ToolRow({
  tool,
  defaultOpen = false,
}: {
  tool: Tool;
  defaultOpen?: boolean;
}) {
  return (
    <details
      open={defaultOpen}
      className={cn("group", tool.hidden && "opacity-60")}
    >
      <summary className="hover:bg-accent/40 flex cursor-pointer select-none flex-wrap items-center gap-2 px-3 py-2 text-sm [&::-webkit-details-marker]:hidden">
        <ChevronRight className="text-muted-foreground size-3 shrink-0 transition-transform group-open:rotate-90" />
        <span className="font-medium">{tool.label}</span>
        <span className="text-muted-foreground font-mono text-xs">
          {tool.ref}
        </span>
        <Chip>{tool.category}</Chip>
        <Chip>{tool.ref_type}</Chip>
        {tool.hidden && <Chip>hidden</Chip>}
        <span className="text-muted-foreground ml-auto text-xs">
          {tool.inputs.length} in · {tool.outputs.length} out
        </span>
      </summary>
      <div className="space-y-3 px-3 pb-3 pl-8 text-sm">
        <p className="text-foreground/85">{tool.description}</p>
        {tool.agent_description && (
          <p className="text-muted-foreground border-l-2 pl-3 text-xs italic">
            {tool.agent_description}
          </p>
        )}
        <Fields label="Inputs" items={tool.inputs} />
        <Fields label="Outputs" items={tool.outputs} />
      </div>
    </details>
  );
}

/* ------------------------------------------------------------------ */
/* Referenced tools (intent detail)                                    */
/* ------------------------------------------------------------------ */

function UnresolvedRef({
  reference,
  note,
  tone = "muted",
}: {
  reference: IntentReference;
  note: string;
  tone?: "muted" | "warn";
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
      {tone === "warn" && (
        <AlertTriangle className="text-destructive size-3.5 shrink-0" />
      )}
      <span className="font-mono text-xs">{reference.ref}</span>
      <Chip>{reference.ref_type}</Chip>
      <span
        className={cn(
          "text-xs",
          tone === "warn" ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {note}
      </span>
    </div>
  );
}

/** A reference the list doesn't carry — resolved one at a time. */
function ResolvedRef({
  tenantId,
  reference,
}: {
  tenantId: string;
  reference: IntentReference;
}) {
  const { data, isLoading, error } = useTool(
    tenantId,
    reference.ref_type,
    reference.ref,
  );
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm">
        <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
        <span className="font-mono text-xs">{reference.ref}</span>
      </div>
    );
  }
  if (data) return <ToolRow tool={data} />;
  const body =
    isAxiosError(error) && error.response?.status === 404
      ? (error.response.data as ToolUnavailable | undefined)
      : undefined;
  return (
    <UnresolvedRef
      reference={reference}
      tone="warn"
      note={
        body
          ? `${body.message} (${body.reason})`
          : "could not resolve — the tools route failed"
      }
    />
  );
}

/** The intent's references, each resolved against the registry. */
export function ReferencedTools({
  tenantId,
  references,
  tools,
}: {
  tenantId: string;
  references: IntentReference[];
  tools: ReturnType<typeof useTools>;
}) {
  if (references.length === 0) return null;
  const byKey = new Map(
    (tools.data ?? []).map((t) => [`${t.ref_type}:${t.ref}`, t]),
  );
  return (
    <section className="space-y-2">
      <SectionHeading note="what the chips in the guidance point at">
        References ({references.length})
      </SectionHeading>
      {tools.isLoading ? (
        <div
          role="status"
          aria-label="Loading tools"
          className="flex h-12 items-center justify-center"
        >
          <Loader2 className="text-muted-foreground size-5 animate-spin" />
        </div>
      ) : (
        <div className="divide-y rounded-lg border">
          {references.map((ref) => {
            const key = `${ref.ref_type}:${ref.ref}`;
            const hit = byKey.get(key);
            if (hit) return <ToolRow key={key} tool={hit} />;
            if (tools.isError) {
              return (
                <UnresolvedRef
                  key={key}
                  reference={ref}
                  tone="warn"
                  note="the tools registry failed to load"
                />
              );
            }
            // Forms and workflows are not in the list; ask for them by ref.
            return (
              <ResolvedRef key={key} tenantId={tenantId} reference={ref} />
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Tools registry (page tab)                                           */
/* ------------------------------------------------------------------ */

type CategoryFilter = "all" | Tool["category"];

export function ToolsRegistry({ tenantId }: { tenantId: string }) {
  const { data, isLoading, isError } = useTools(tenantId);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [showHidden, setShowHidden] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data ?? [])
      .filter((t) => category === "all" || t.category === category)
      .filter((t) => showHidden || !t.hidden)
      .filter(
        (t) =>
          !q ||
          t.label.toLowerCase().includes(q) ||
          t.ref.toLowerCase().includes(q),
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data, query, category, showHidden]);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading tools"
        className="flex h-64 items-center justify-center"
      >
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="border-destructive/30 text-muted-foreground flex h-56 items-center justify-center rounded-xl border border-dashed text-sm"
      >
        Failed to load the tools registry.
      </div>
    );
  }

  const hiddenCount = (data ?? []).filter((t) => t.hidden).length;
  return (
    <div className="bg-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border">
      <div className="flex flex-wrap items-center gap-2 border-b p-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder="Search tools…"
            className="pl-8"
          />
        </div>
        <Tabs
          value={category}
          onValueChange={(v) => {
            setCategory(v as CategoryFilter);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="read">Read</TabsTrigger>
            <TabsTrigger value="action">Action</TabsTrigger>
          </TabsList>
        </Tabs>
        {hiddenCount > 0 && (
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => {
                setShowHidden(e.target.checked);
              }}
            />
            show hidden ({hiddenCount})
          </label>
        )}
        <span className="text-muted-foreground text-xs">
          {filtered.length} of {data?.length ?? 0}
        </span>
      </div>
      <div className="min-h-0 flex-1 divide-y overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-muted-foreground p-4 text-center text-sm">
            No tool matches the filter.
          </p>
        ) : (
          filtered.map((tool) => (
            <ToolRow key={`${tool.ref_type}:${tool.ref}`} tool={tool} />
          ))
        )}
      </div>
    </div>
  );
}
